/* jshint esversion: 6 */
/**
 * QPResponsePDFValidator class is responsible for validating PDF responses for question parts.
 * It uses various services and models to perform its validation logic.
 */
class QPResponsePDFValidator {
    /**
     * Constructor for QPResponsePDFValidator.
     * @param {Object} model - The model object.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} ContentObjectRegistryService - Service for content object registry.
     */
    constructor(model, $injector, $timeout, ContentObjectRegistryService) {
        this.model = model;
        this.$injector = $injector;
        this.$timeout = $timeout;
        this.ContentObjectRegistryService = ContentObjectRegistryService;

        this._TaskDataService = null;
        this._ValidatorService = null;
        this._translate = null;
        this._fetchContext = null;
        this._messageTranslateString = 'CMS.FACTORIES.MODELS.VALIDATORS.LOGIC.QP-RESPONSE-PDF';
        this._messageQPNotUsedTranslateString = 'CMS.FACTORIES.MODELS.VALIDATORS.LOGIC.QP-NOT-USED';
        this._message = null;
        this._questionPartNotUsed = false;
        this._forced_check = false;
    }

    /**
     * Lazy loads and returns the TaskDataService.
     * @returns {Object} TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Lazy loads and returns the ValidatorService.
     * @returns {Object} ValidatorService instance.
     */
    getValidatorService() {
        if (!this._ValidatorService) {
            this._ValidatorService = this.$injector.get('ValidatorService');
        }
        return this._ValidatorService;
    }

    /**
     * Lazy loads and returns the $translate service.
     * @returns {Object} $translate service instance.
     */
    getTranslateService() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Lazy loads and returns the fetch context.
     * @returns {Object} Fetch context instance.
     */
    getFetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.getTaskDataService().fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Validates sub-containers within the main container.
     * @param {Array} sub_container_array - Array of sub-containers.
     * @param {number} includedInPDF_object_counter - Counter for objects included in PDF.
     * @returns {boolean} True if there is an error, false otherwise.
     */
    _validateSubContainer(sub_container_array, includedInPDF_object_counter) {
        let error = true;
        let container_objects_counter = 0;
        for (let sub_container_key in sub_container_array) {
            let sub_container = sub_container_array[sub_container_key];
            if (!angular.isUUID(sub_container.content)) {
                continue;
            }
            sub_container = this.getFetchContext().read(sub_container.content);
            for (let key in sub_container.getObjects()) {
                let object = sub_container.getObjects()[key];
                let preferences;

                if (angular.isUUID(object)) {
                    object = this.getFetchContext().read(sub_container.getObjects()[key]);
                    preferences = object.getPreferences();
                } else {
                    preferences = object.preferences;
                }

                if (preferences && preferences.includedInPDF === true) {
                    error = false;
                    includedInPDF_object_counter++;
                }
                container_objects_counter++;
            }
        }

        if (container_objects_counter === 0) return false;

        return error;
    }

    /**
     * Main validation method.
     * @param {Object} from - The source object for validation.
     * @returns {Object} Validation result.
     */
    validate(from) {
        let result = {};
        let error = true;
        if (from && this.model.getUUID() === from.getUUID()) {
            return result;
        } else if (this.model.getType() === 'layout') {
            let rows = this.model.getRows();
            for (let row_key in rows) {
                let row = rows[row_key];

                for (let col_key in row.cols) {
                    let col = row.cols[col_key];

                    for (let list_key in col.list) {
                        let list = col.list[list_key];

                        let container;
                        let container_uuid = list.content;
                        if (angular.isUUID(container_uuid)) {
                            container = this.getFetchContext().read(list.content);
                        }
                        if (container && container.getQuestionPart()) {
                            if (this.getValidatorService().getCurrentTask()) {
                                container.getQuestionPart().validate(this.model);
                            }
                        }
                    }
                }
            }

            return result;

        } else if (this.model.getType() !== 'questionPart') {
            let parents = this.model.getParents();
            let question_parts = _.filter(parents, function (item) {
                return item.getType() === 'questionPart';
            });

            for (let key in question_parts) {
                let question_part_object = question_parts[key];
                question_part_object = this.getFetchContext().read(question_part_object.getUUID());
                if (this.getValidatorService().getCurrentTask()) {
                    question_part_object.validate(this.model);
                }
            }

            return result;
        }

        let parents = this.model.getParents();
        let containers = _.filter(parents, function (item) {
            return item.getType() === 'container';
        });

        if (containers.length > 0 && from && from.getType() !== 'questionPart') {
            let sheet = _.find(parents, function (item) {
                return item.getType() === 'sheet';
            });

            if (sheet) {
                let question_parts = sheet.getQuestionParts();
                for (let key in question_parts) {
                    let question_part_object = question_parts[key];
                    this.$timeout(() => {
                        if (this.getValidatorService().getCurrentTask()) {
                            question_part_object.validate(this.model);
                        }
                    });
                }
            }
        }

        if (containers.length === 0) {
            this._questionPartNotUsed = true;
            result = {
                'onSheetBlur': true,
                'onlyBanner': true,
                'validator': this.getValidatorName(),
                'modelUUID': this.model.getUUID(),
                'error': error,
                'status': error ? 'DANGER' : 'SUCCESS', // WARNING | DANGER | SUCCESS
                'message': this.getErrorMessage(),
                'message_data': {
                    'qp_data': {
                        'title': this.model.getTitle(),
                        'shortTitle': this.model.getShortTitle(),
                        'useCustomTitle': this.model.isUseCustomTitle()
                    }
                },
                'callbacks': [
                    'validationDisplayContainerHeadline-qp-' + this.model.getUUID()
                ]
            };
            let sheet = _.find(parents, function (item) {
                return item.getType() === 'sheet';
            });
            if (sheet) {
                result['message_data']['sheet_data'] = {
                    'uuid': sheet.getUUID(),
                    'title': sheet.getTitle(),
                    'shortTitle': sheet.getShortTitle(),
                    'useCustomTitle': sheet.isUseCustomTitle()
                };
            }

            return result;
        }

        if (this.model.getType() === 'questionPart' && this.model.data.isDefaultQuestionPart === true) {
            error = false;
        } else {
            let includedInPDF_object_counter = 0;
            for (let key in containers) {
                let container = containers[key];

                if (!container.getQuestionPart()) continue;
                if (container.getQuestionPart().getUUID() !== this.model.getUUID()) continue;

                if (container.isShowTabs()) {
                    error = includedInPDF_object_counter > 0 ? false : this._validateSubContainer(container.getTabs(), includedInPDF_object_counter);
                } else if (container.isShowCarousels()) {
                    error = includedInPDF_object_counter > 0 ? false : this._validateSubContainer(container.getCarousels(), includedInPDF_object_counter);
                } else if (container.isShowIaaContainer()) {
                    error = includedInPDF_object_counter > 0 ? false : this._validateSubContainer(container.getIaaContainer(), includedInPDF_object_counter);
                } else {
                    let container_objects_counter = 0;
                    for (let key in container.getObjects()) {
                        let object = container.getObjects()[key];

                        let preferences;

                        if (angular.isUUID(object)) {
                            object = this.getFetchContext().read(object);
                            preferences = object.getPreferences();
                        } else {
                            preferences = object.preferences;
                        }

                        let includedInPDF = preferences ? preferences.includedInPDF : undefined;

                        if (!angular.isDefined(includedInPDF)) {
                            let objTypeProps = this.ContentObjectRegistryService.getObjectProperties(object.getObjectType());
                            if (angular.isDefined(objTypeProps) && angular.isDefined(objTypeProps.obj) && angular.isDefined(objTypeProps.obj.preferences)) {
                                includedInPDF = objTypeProps.obj.preferences.includedInPDF;
                            }
                        }

                        if (includedInPDF === true) {
                            error = false;
                            includedInPDF_object_counter++;
                        }

                        container_objects_counter++;
                    }
                    if (container_objects_counter === 0 && error) {
                        error = false;
                    }
                }
            }
        }

        let sheet = _.find(parents, function (item) {
            return item.getType() === 'sheet';
        });
        this._questionPartNotUsed = false;
        result = {
            'onSheetBlur': true,
            'onlyBanner': false,
            'validator': this.getValidatorName(),
            'modelUUID': this.model.getUUID(),
            'error': error,
            'status': error ? 'DANGER' : 'SUCCESS', // WARNING | DANGER | SUCCESS
            'message': this.getErrorMessage(),
            'message_template': false,
            'message_data': {
                'qp_data': {
                    'title': this.model.getTitle(),
                    'shortTitle': this.model.getShortTitle(),
                    'useCustomTitle': this.model.isUseCustomTitle()
                }
            },
            'from': from ? from.data : null,
            'callbacks': [
                'validationDisplayContainerHeadline-qp-' + this.model.getUUID()
            ]
        };

        if (sheet) {
            result['message_data']['sheet_data'] = {
                'uuid': sheet.getUUID(),
                'title': sheet.getTitle(),
                'shortTitle': sheet.getShortTitle(),
                'useCustomTitle': sheet.isUseCustomTitle()
            };
        }

        return result;
    }

    /**
     * Returns the error message based on the validation state.
     * @returns {string} Error message.
     */
    getErrorMessage() {
        let questionHeadline = '';
        if (this._questionPartNotUsed) {
            let sheet = _.find(this.model.getParents(), function (item) {
                return item.getType() === 'sheet';
            });
            if (this.model.getTitle()) {
                questionHeadline = this.model.getTitle() + ' (in ' + this.getTranslateService().instant("PREFIX.QUESTION") + ' ' + (sheet.getTitle() ? sheet.getTitle() : sheet.getShortTitle()) + ')';
            } else {
                questionHeadline = this.model.getTitle() ? (this.model.getTitle()) : (sheet.getTitle() ? (sheet.getTitle() + '  ' + sheet.getShortTitle() + this.model.getShortTitle()) : ((sheet.getShortTitle() + this.model.getShortTitle())));
            }
        } else {
            questionHeadline = this.model.getQuestionHeadline();
        }
        this._message = this.getTranslateService().instant(this._questionPartNotUsed ? this._messageQPNotUsedTranslateString : this._messageTranslateString, {
            s: questionHeadline ? questionHeadline : ''
        });

        return this._message;
    }

    /**
     * Returns the forced check status.
     * @returns {boolean} Forced check status.
     */
    forcedCheck() {
        return this._forced_check;
    }

    /**
     * Returns the validator name.
     * @returns {string} Validator name.
     */
    getValidatorName() {
        return 'QPResponsePDFValidator';
    }
}

export default QPResponsePDFValidator;